import React, { useState } from "react";
import { Alert, Button, Form, Input, Radio, Space, Typography } from "antd";
import { startMfaRecovery, verifyMfaRecovery } from "../../api/auth/mfaApi";
import { finishSignIn, type SignInResult } from "../../utils/auth/sessionUtilities";
import { attemptsRemaining, authFailure, authFailureMessage, hasFieldError } from "../../lib/http/authErrors";
import type { LoginMfaRequired } from "../../lib/http/session";
import type { CodeSent, RecoveryMethod } from "../../types/auth/mfa";
import OtpInput from "../../components/auth/mfa/OtpInput";
import { isOtp, isRecoveryCode, normaliseOtp, normaliseRecoveryCode } from "../../components/auth/mfa/codes";
import { CHALLENGE_EXPIRED_MESSAGE } from "./SecondFactorStep";

const { Paragraph, Text } = Typography;

const METHOD_LABELS: Record<RecoveryMethod, string> = {
	recovery_code: "Use a recovery code",
	sms: "Text a code to my recovery phone",
	email: "Email a code to me",
};

const ORDER: RecoveryMethod[] = ["recovery_code", "sms", "email"];

interface RecoveryStepProps {
	challenge: LoginMfaRequired;
	onResult: (result: SignInResult) => void;
	onRestart: (message?: string) => void;
	onBack: () => void;
}

// Minutes left on the sent code, rounded up (contract §6.3); identity extends the challenge to the same instant.
const codeExpiryNotice = (sent: CodeSent, now: number = Date.now()): string => {
	const minutes = Math.ceil((Date.parse(sent.expires_at) - now) / 60_000);
	if (!Number.isFinite(minutes) || minutes < 1) return `Code sent to ${sent.masked_destination}.`;
	return `Code sent to ${sent.masked_destination}. It expires in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`;
};

const recoveryError = (error: unknown, method: RecoveryMethod): string => {
	const failure = authFailure(error);
	if (failure.code === "invalid_credentials") {
		const left = attemptsRemaining(failure);
		const base = method === "recovery_code" ? "That recovery code is not valid or was already used." : "That code is incorrect.";
		return left === undefined ? base : `${base} ${left} ${left === 1 ? "attempt" : "attempts"} left.`;
	}
	if (failure.status === 422 && hasFieldError(failure, "method")) {
		return method === "sms" ? "This account has no recovery phone. Choose another way." : "That way to recover is not available. Choose another.";
	}
	return authFailureMessage(error);
};

// "Lost your device?" (contract §6.3): a recovery code, or a code by SMS or email, opens a session confined to enrolling a new factor.
const RecoveryStep: React.FC<RecoveryStepProps> = ({ challenge, onResult, onRestart, onBack }) => {
	const methods = ORDER.filter((m) => challenge.recovery_methods.includes(m));
	const [method, setMethod] = useState<RecoveryMethod>(methods[0] ?? "recovery_code");
	const [sent, setSent] = useState<CodeSent | null>(null);
	const [busy, setBusy] = useState<"send" | "verify" | null>(null);
	const [error, setError] = useState("");
	const [form] = Form.useForm<{ code: string }>();
	const needsSend = method === "sms" || method === "email";

	const fail = (err: unknown) => {
		if (authFailure(err).code === "challenge_expired") {
			onRestart(CHALLENGE_EXPIRED_MESSAGE);
			return;
		}
		setError(recoveryError(err, method));
	};

	const chooseMethod = (next: RecoveryMethod) => {
		setMethod(next);
		setSent(null);
		setError("");
		form.resetFields();
	};

	const sendCode = async () => {
		if (method === "recovery_code") return;
		setBusy("send");
		setError("");
		try {
			setSent(await startMfaRecovery(challenge.challenge_id, method));
		} catch (err) {
			fail(err);
		} finally {
			setBusy(null);
		}
	};

	const verify = async ({ code }: { code: string }) => {
		setBusy("verify");
		setError("");
		try {
			const value = method === "recovery_code" ? normaliseRecoveryCode(code) : normaliseOtp(code);
			onResult(await finishSignIn(await verifyMfaRecovery(challenge.challenge_id, method, value)));
		} catch (err) {
			form.setFieldValue("code", "");
			fail(err);
		} finally {
			setBusy(null);
		}
	};

	const codeValid = (value: string) => (method === "recovery_code" ? isRecoveryCode(value) : isOtp(value));

	return (
		<div className="login-form" data-testid="login-recovery-step">
			<Paragraph className="login-step-intro">
				Recover your account, then set up a new authenticator app or passkey. Text and email codes are for recovery only.
			</Paragraph>

			{methods.length > 1 && (
				<Radio.Group
					className="login-form-item login-recovery-methods"
					value={method}
					onChange={(e) => chooseMethod(e.target.value)}
					aria-label="Way to recover your account"
					disabled={busy !== null}
				>
					<Space direction="vertical">
						{methods.map((m) => (
							<Radio key={m} value={m} data-testid={`login-recovery-method-${m}`}>
								{METHOD_LABELS[m]}
							</Radio>
						))}
					</Space>
				</Radio.Group>
			)}

			{error && (
				<div className="login-form-item">
					<Alert data-testid="login-recovery-error" message={error} type="error" showIcon />
				</div>
			)}

			{needsSend && !sent && (
				<div className="login-form-item">
					<Button data-testid="login-recovery-send" type="primary" block size="large" onClick={sendCode} loading={busy === "send"}>
						{method === "sms" ? "Text me a code" : "Email me a code"}
					</Button>
				</div>
			)}

			{(!needsSend || sent) && (
				<Form form={form} name="recovery" layout="vertical" size="large" onFinish={verify} requiredMark={false}>
					{sent && (
						<div className="login-form-item">
							<Alert
								data-testid="login-recovery-sent"
								type="info"
								showIcon
								message={codeExpiryNotice(sent)}
							/>
						</div>
					)}
					<Form.Item
						name="code"
						label={method === "recovery_code" ? "Recovery code" : "6-digit code"}
						className="login-form-item"
						rules={[{ validator: (_, value) => (codeValid(value ?? "") ? Promise.resolve() : Promise.reject(new Error(method === "recovery_code" ? "Enter a code like 7KQ2-M9XD" : "Enter the 6-digit code"))) }]}
					>
						{method === "recovery_code" ? (
							<Input data-testid="login-recovery-code" placeholder="XXXX-XXXX" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={9} autoFocus />
						) : (
							<OtpInput data-testid="login-recovery-code" autoFocus />
						)}
					</Form.Item>
					<Form.Item className="login-form-item">
						<Button data-testid="login-recovery-submit" type="primary" htmlType="submit" block loading={busy === "verify"} disabled={busy === "send"}>
							Continue
						</Button>
					</Form.Item>
					{sent && (
						<Text type="secondary">
							No code?{" "}
							<Button type="link" size="small" onClick={sendCode} loading={busy === "send"} style={{ padding: 0 }}>
								Send another
							</Button>
						</Text>
					)}
				</Form>
			)}

			<div className="login-step-links">
				<Button data-testid="login-recovery-back" type="link" onClick={onBack} disabled={busy !== null}>
					Use my authenticator instead
				</Button>
			</div>
		</div>
	);
};

export default RecoveryStep;
